import { computed, effect, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { IPersistentAbstractControl, IPersistentModelControl } from '@utility/persistent-forms';
import { filter, merge, Observable, switchMap } from 'rxjs';

type UnwrapSignals<T extends Record<string, Signal<unknown>>> = {
    [K in keyof T]: T[K] extends Signal<infer V> ? V : never;
};

type AllDefined<T> = {
    [K in keyof T]: Exclude<T[K], undefined>;
};
type AllDefinedNotNull<T> = {
    [K in keyof T]: Exclude<T[K], undefined | null>;
};
/**
 * Creates a computed signal that returns `undefined` if any input signal is `undefined`,
 * otherwise passes all defined values to the computation function.
 *
 * @example
 * const result = computedAllDefined(
 *   { input1: mySignal1, input2: mySignal2 },
 *   ({ input1, input2 }) => input1 + input2
 * );
 */
export function computedAllDefined<TSignals extends Record<string, Signal<unknown>>, TResult>(
    signals: TSignals,
    computation: (values: AllDefined<UnwrapSignals<TSignals>>) => TResult
): Signal<TResult | undefined> {
    return computed(() => {
        const values: Record<string, unknown> = {};
        for (const key in signals) {
            const value = signals[key]();
            if (value === undefined) {
                return undefined;
            }
            values[key] = value;
        }
        return computation(values as AllDefined<UnwrapSignals<TSignals>>);
    });
}

/**
 * Creates a computed signal that returns `undefined` if any input signal is `undefined`,
 * otherwise passes all defined values to the computation function.
 *
 * @example
 * const result = computedAllDefined(
 *   { input1: mySignal1, input2: mySignal2 },
 *   ({ input1, input2 }) => input1 + input2
 * );
 */
export function computedAllDefinedNotNull<TSignals extends Record<string, Signal<unknown>>, TResult>(
    signals: TSignals,
    computation: (values: AllDefinedNotNull<UnwrapSignals<TSignals>>) => TResult
): Signal<TResult | null | undefined> {
    return computed(() => {
        const values: Record<string, unknown> = {};
        for (const key in signals) {
            const value = signals[key]();
            if (value === undefined) {
                return undefined;
            }
            if (value === null) {
                return null;
            }
            values[key] = value;
        }
        return computation(values as AllDefinedNotNull<UnwrapSignals<TSignals>>);
    });
}

/**
 * Creates an effect that only runs the effect function when all input signals have defined values.
 * If any input signal is `undefined`, the effect body is skipped.
 *
 * @example
 * effectAllDefined(
 *   { input1: mySignal1, input2: mySignal2 },
 *   ({ input1, input2 }) => console.log(input1, input2)
 * );
 */
export function effectAllDefined<TSignals extends Record<string, Signal<unknown>>>(
    signals: TSignals,
    effectFn: (values: AllDefined<UnwrapSignals<TSignals>>) => void
): ReturnType<typeof effect> {
    return effect(() => {
        const values: Record<string, unknown> = {};
        for (const key in signals) {
            const value = signals[key]();
            if (value === undefined) {
                return;
            }
            values[key] = value;
        }
        effectFn(values as AllDefined<UnwrapSignals<TSignals>>);
    });
}

/**
 * Creates an effect that only runs the effect function when all input signals have defined values.
 * If any input signal is `undefined`, the effect body is skipped.
 *
 * @example
 * effectAllDefined(
 *   { input1: mySignal1, input2: mySignal2 },
 *   ({ input1, input2 }) => console.log(input1, input2)
 * );
 */
export function effectAllDefinedNotNull<TSignals extends Record<string, Signal<unknown>>>(
    signals: TSignals,
    effectFn: (values: AllDefinedNotNull<UnwrapSignals<TSignals>>) => void
): ReturnType<typeof effect> {
    return effect(() => {
        const values: Record<string, unknown> = {};
        for (const key in signals) {
            const value = signals[key]();
            if (value === undefined) {
                return;
            }
            if (value === null) {
                return;
            }
            values[key] = value;
        }
        effectFn(values as AllDefinedNotNull<UnwrapSignals<TSignals>>);
    });
}

export function toSignalFromObservable<TSource, TValue>(
    source: Signal<TSource | null | undefined>,
    selector: (value: TSource) => Observable<TValue>
) {
    return toSignal(
        toObservable(source).pipe(
            filter(Boolean),
            switchMap((value) => selector(value))
        )
    );
}

export function patchFormEffect<
    TFormGroup extends AbstractControl<any> & IPersistentAbstractControl,
    TParams = {},
>(opts: {
    formGroup: () => TFormGroup | null;
    params: () => TParams | undefined;
    value: (opts: TParams & { formGroup: TFormGroup }) => TFormGroup['value'];
}) {
    return effect(() => {
        const formGroup = opts.formGroup();
        const params = opts.params();
        if (params === undefined) return;
        if (formGroup === null) return;

        formGroup.programmaticPatchValue(opts.value({ ...params, formGroup }));
        // if (formGroup.dirty) return; // skip if form has been modified, to avoid overwriting user input when model changes for other reasons than initial load or reset after save
        // formGroup.reset();
        // opts.patchFn({ ...params, formGroup });
        // formGroup.markAsDirty({ onlySelf: true }); // mark as dirty after patching to allow subsequent patches to run if model changes again, while still preventing patches from running on user input
    });
}

export function patchFormWithModelEffect<
    TFormGroup extends AbstractControl<any> & IPersistentModelControl<TModel> & IPersistentAbstractControl,
    TModel,
    TParams = {},
>(opts: {
    formGroup: () => TFormGroup | null;
    params: () => TParams | undefined;
    value: (opts: TParams & { formGroup: TFormGroup }) => TFormGroup['value'];
}) {
    return effect(() => {
        const formGroup = opts.formGroup();
        const params = opts.params();
        if (params === undefined) return;
        if (formGroup === null) return;
        const model = formGroup.model();
        if (model === null || model === undefined) return;
        if (model === formGroup.lastPatchedModel) return;

        // remember last model it was invoked with, and only run if model changes, to avoid running on other param changes which can cause unintended overwriting of user input in the form when model doesn't change but other params do, for example when Make Primary is clicked and the form is patched with the same model but different params

        formGroup.programmaticPatchValue(opts.value({ ...params, formGroup }));
        formGroup.lastPatchedModel = model;
        // if (formGroup.dirty) return; // skip if form has been modified, to avoid overwriting user input when model changes for other reasons than initial load or reset after save
        // formGroup.reset();
        // opts.patchFn({ ...params, formGroup });
        // formGroup.markAsDirty({ onlySelf: true }); // mark as dirty after patching to allow subsequent patches to run if model changes again, while still preventing patches from running on user input
    });
}

export function disableFormEffect<TFormGroup extends IPersistentAbstractControl>(opts: {
    form: () => TFormGroup | null;
    disabled: () => boolean | null | undefined;
}) {
    return effect(() => {
        const formGroup = opts.form();
        if (formGroup === null) return;

        const disabled = opts.disabled();
        if (disabled === undefined || disabled === null) return;

        if (disabled) {
            formGroup.disable({ persistent: true });
        } else {
            formGroup.enable({ breakPersistence: true });
        }
    });
}

export function mergeSignals<GValue>(...signals: Signal<GValue>[]): Signal<GValue> {
    return toSignal(merge(...signals.map((s) => toObservable(s))), { initialValue: null as GValue });
}
